/**
 * Login Component
 *
 * Production mode: Opens Cornerstone SSO in a popup window.
 * After authentication, the popup stores tokens and signals via localStorage.
 * The parent window detects the signal and redirects to dashboard.
 *
 * Kiosk mode: Local PIN login for check-in devices.
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { Button, Input } from '../../design-system-integration';
import { useAuth } from '../../context/AuthContext';
import type { User } from '../../context/AuthContext';
import { message } from 'antd';
import { enhancedTokenManager } from '../../services/EnhancedTokenManager';
import { authStateManager } from '../../utils/authStateManager';

const Title = styled.h1`
  font-size: var(--font-size-2xl);
  font-weight: var(--font-weight-bold);
  color: var(--text-primary);
  margin-bottom: var(--spacing-md);
  text-align: center;
`;

const Subtitle = styled.p`
  font-size: var(--font-size-md);
  color: var(--text-secondary);
  margin-bottom: var(--spacing-xl);
  text-align: center;
`;

const LoginForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-lg);
`;

const FormActions = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-md);
`;

const SubmitWrapper = styled.div`
  width: 100%;
  display: block;
`;

const ErrorMessage = styled.div`
  color: var(--error-default);
  font-size: var(--font-size-sm);
  padding: var(--spacing-sm);
  background-color: var(--error-light);
  border-radius: 4px;
  margin-bottom: var(--spacing-md);
`;

const CornerstoneButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--spacing-sm);
  width: 100%;
  padding: 12px var(--spacing-md);
  border: none;
  border-radius: 6px;
  background: #1e3a5f;
  color: white;
  font-size: var(--font-size-md);
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: var(--primary-600, #162d4a);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

interface LoginFixedProps {
  isKioskMode?: boolean;
}

const POPUP_WIDTH = 500;
const POPUP_HEIGHT = 650;

const LoginFixed: React.FC<LoginFixedProps> = ({ isKioskMode = false }) => {
  const [email, setEmail] = useState('');
  const [pin, setPin] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const { refreshUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from?.pathname || '/dashboard';

  // Load tokens from localStorage, fetch user profile, then navigate
  const completeAuth = useCallback(async () => {
    // Check for preflight or API errors from the popup
    const authError = localStorage.getItem('cornerstone_auth_error');
    if (authError) {
      localStorage.removeItem('cornerstone_auth_error');
      localStorage.removeItem('cornerstone_auth_complete');
      // ADM-014: Navigate to dedicated preflight error page with diagnostic state
      // (replaces previous in-place error string with a full diagnostic surface).
      try {
        const errorData = JSON.parse(authError);
        navigate('/auth/cornerstone/preflight-error', {
          replace: true,
          state: {
            error: errorData.error,
            message: errorData.message,
            missing_fields: errorData.missing_fields,
          },
        });
      } catch {
        navigate('/auth/cornerstone/preflight-error', {
          replace: true,
          state: { error: 'default', message: 'Sign-in failed. Please try again.' },
        });
      }
      setIsLoading(false);
      return;
    }

    const accessToken = localStorage.getItem('access_token');
    const expiresAt = localStorage.getItem('token_expires_at');
    if (!accessToken || !expiresAt) return;

    // 1. Set token in memory
    enhancedTokenManager.setToken(accessToken, expiresAt);
    localStorage.removeItem('cornerstone_auth_complete');

    // 2. Fetch user profile (checkAuth requires a stored user to work)
    try {
      const res = await fetch('/api/v1/auth/users/me/', {
        headers: { 'Authorization': `Bearer ${accessToken}` },
      });
      if (res.ok) {
        const userData = await res.json();
        // Store user so checkAuth finds it
        const transformedUser = {
          id: userData.id,
          email: userData.email,
          name: userData.name || `${userData.first_name || ''} ${userData.last_name || ''}`.trim(),
          role: userData.role,
          churchId: userData.church_id || (typeof userData.church === 'object' ? userData.church?.id : userData.church),
          is_superuser: userData.is_superuser,
        };
        authStateManager.setUser(transformedUser as any);
        sessionStorage.setItem('connectpath_active_session', 'true');
      }
    } catch {
      // Best effort — refreshUser will retry
    }

    // 3. Refresh auth state and navigate
    try {
      await refreshUser();
    } catch {
      // Ignore — we have tokens
    }

    // 4. Check if user has a church — if not, show setup message
    const storedUser = authStateManager.getUser();
    const churchId = (storedUser as any)?.churchId || (storedUser as any)?.church_id;
    if (!churchId) {
      setError('Unable to load your organization. Please try again.');
      setIsLoading(false);
      return;
    }

    // 5. Force navigate (don't rely on React state update)
    window.location.href = from;
  }, [from, refreshUser]);

  // Listen for the popup to signal auth completion via localStorage
  useEffect(() => {
    const handleStorage = (event: StorageEvent) => {
      if (event.key === 'cornerstone_auth_complete' && event.newValue) {
        if (pollRef.current) clearInterval(pollRef.current);
        completeAuth();
      }
    };

    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, [completeAuth]);

  const openCornerstonePopup = () => {
    setError(null);
    setIsLoading(true);

    // Clear any stale signal
    localStorage.removeItem('cornerstone_auth_complete');

    const authorizeUrl = '/api/v1/cornerstone/oauth/authorize/?return_url=/auth/cornerstone/callback';

    // Center the popup
    const left = window.screenX + (window.outerWidth - POPUP_WIDTH) / 2;
    const top = window.screenY + (window.outerHeight - POPUP_HEIGHT) / 2;

    const popup = window.open(
      authorizeUrl,
      'cornerstone-login',
      `width=${POPUP_WIDTH},height=${POPUP_HEIGHT},left=${left},top=${top},scrollbars=yes,resizable=yes`
    );

    if (!popup) {
      // Popup blocked — fall back to full redirect
      window.location.href = authorizeUrl;
      return;
    }

    // Poll to detect if popup was closed without completing auth
    pollRef.current = setInterval(() => {
      if (popup.closed) {
        if (pollRef.current) clearInterval(pollRef.current);

        // Check if auth completed (tokens stored by popup)
        const authComplete = localStorage.getItem('cornerstone_auth_complete');
        if (authComplete) {
          completeAuth();
        } else {
          setIsLoading(false);
        }
      }
    }, 500);
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, []);

  const handleKioskSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email || !pin) {
      setError('Please enter both email and PIN');
      return;
    }

    try {
      setError(null);
      setIsLoading(true);

      const response = await fetch('/api/v1/auth/users/kiosk_login/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, pin }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || data.error?.message || 'Invalid credentials');
      }

      const authResponse = {
        access_token: data.access,
        user: data.user,
        expires_at: data.expires_at || new Date(Date.now() + 3600000).toISOString(),
      };

      enhancedTokenManager.setToken(authResponse.access_token, authResponse.expires_at);

      const transformedUser: User = {
        id: authResponse.user.id,
        email: authResponse.user.email,
        name: `${authResponse.user.first_name} ${authResponse.user.last_name}`.trim(),
        role: authResponse.user.role,
        churchId: authResponse.user.church,
        is_superuser: authResponse.user.is_superuser,
      };

      authStateManager.setUser(transformedUser);
      localStorage.setItem('isKioskMode', 'true');

      message.success('Kiosk login successful');
      navigate('/kiosk/checkin');
    } catch (err: any) {
      setError(err.message || 'Failed to login.');
    } finally {
      setIsLoading(false);
    }
  };

  if (isKioskMode) {
    return (
      <div>
        <Title>ConnectPath Kiosk</Title>
        <Subtitle>Sign in with your kiosk PIN to access check-in</Subtitle>

        {error && <ErrorMessage>{error}</ErrorMessage>}

        <LoginForm onSubmit={handleKioskSubmit}>
          <Input
            label="Email"
            type="email"
            value={email}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEmail(e.target.value)}
            placeholder="Enter your email"
            fullWidth
            required
            autoComplete="email"
          />
          <Input
            label="PIN"
            type="password"
            value={pin}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPin(e.target.value)}
            placeholder="Enter your 6-digit PIN"
            fullWidth
            required
            maxLength={6}
            pattern="[0-9]{6}"
            autoComplete="off"
          />
          <FormActions>
            <SubmitWrapper>
              <Button
                type="submit"
                variant="primary"
                fullWidth
                disabled={isLoading}
              >
                {isLoading ? 'Signing in...' : 'Access Kiosk'}
              </Button>
            </SubmitWrapper>
          </FormActions>
        </LoginForm>
      </div>
    );
  }

  // Production: Cornerstone SSO via popup window
  return (
    <div>
      <Title>Welcome Back</Title>
      <Subtitle>Sign in to your ConnectPath account</Subtitle>

      {error && <ErrorMessage>{error}</ErrorMessage>}

      <FormActions>
        <CornerstoneButton
          type="button"
          disabled={isLoading}
          onClick={openCornerstonePopup}
        >
          {isLoading ? 'Signing in...' : 'Sign In'}
        </CornerstoneButton>
      </FormActions>
    </div>
  );
};

export default LoginFixed;
